import { KonvaEventObject } from "konva/lib/Node";
import { RefObject, useCallback, useEffect, useState } from "react";
import { useEditorStore } from "../stores/use-editor-store";
import { toast } from "@/components/ui/toast";
import { Region } from "@/api/studio/data/types/region.type";
import { processRegionsForPaste } from "@/features/studies/utils/paste-regions";
import { useSegment } from "@/hooks/use-segment";
import { StudyType } from "@/api/studio/studies/types/study.type";

interface UseContextMenuOptions {
  projectId: string;
  studyId: string;
  studyType: StudyType;
}

/*
 * Custom hook for handling context menu actions in the editor
 * Provides functionality for copying, pasting, and deleting regions
 * Also manages the position of the context menu
 */
export const useContextMenu = (editorRef: RefObject<HTMLDivElement>, options: UseContextMenuOptions) => {
  const { projectId, studyId, studyType } = options;

  /* State */
  const [contextMenuPos, setContextMenuPos] = useState<{ x: number; y: number } | null>(null);

  /* Hooks */
  const { segmentTrackEvent } = useSegment();

  /* Store actions */
  const { setSelectedRegion, deleteRegion, addRegion } = useEditorStore((state) => state.editorActions);

  /* Stores */
  const assetIndex = useEditorStore((state) => state.assetIndex);
  const allAssetRegionData = useEditorStore((state) => state.allAssetRegionData);
  const selectedRegion = useEditorStore((state) => state.selectedRegion);
  const sidePanelDisplay = useEditorStore((state) => state.sidePanelDisplay);

  // Hide context menu on user clicks elsewhere
  useEffect(() => {
    const handleClick = () => setContextMenuPos(null);
    if (contextMenuPos) {
      window.addEventListener("click", handleClick);
      return () => window.removeEventListener("click", handleClick);
    }
  }, [contextMenuPos]);

  /* Handlers */
  const handleContextMenu = (e: KonvaEventObject<PointerEvent>) => {
    e.evt.preventDefault();
    // Get bounding rect of canvas for absolute positioning
    const boundingRect = editorRef.current?.getBoundingClientRect();
    setContextMenuPos({
      x: boundingRect ? e.evt.clientX - boundingRect.left : e.evt.clientX,
      y: boundingRect ? e.evt.clientY - boundingRect.top : e.evt.clientY,
    });
    setSelectedRegion(null);
  };

  const handleCopy = useCallback(async () => {
    if (!selectedRegion) return;
    const region = allAssetRegionData[assetIndex].regions?.find((r) => r.id === selectedRegion);
    if (region) {
      try {
        // Copy a single region type
        await navigator.clipboard.writeText(JSON.stringify({ type: "single", region }));

        // Segment event
        segmentTrackEvent("AOIs Copied", {
          numOfAOIs: 1,
          projectId,
          studyId,
          studyType,
          location: "canvas",
        });
        toast({
          title: "AOI copied to clipboard",
          type: "success",
        });
      } catch {
        toast({
          title: "AOI failed to copy",
          type: "error",
        });
      }
    }
    setContextMenuPos(null);
  }, [selectedRegion, setContextMenuPos, allAssetRegionData, assetIndex, segmentTrackEvent, projectId, studyId, studyType]);

  const handlePaste = useCallback(async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (!text) throw new Error("empty clipboard");
      const payload = JSON.parse(text);

      if (payload.type === "multi" && Array.isArray(payload.regions)) {
        // Multi-region paste with clamping
        const targetMeta = allAssetRegionData[assetIndex]?.metaData;

        // Validate that we have the necessary metadata to process the regions and that there are regions to paste
        if (targetMeta && payload.regions.length > 0) {
          const existingLabels = sidePanelDisplay.map((r) => r.label);
          const { regionsToAdd, skipped } = processRegionsForPaste(payload.regions as Region[], targetMeta, existingLabels);
          const pasted = regionsToAdd.length;

          // Add the processed regions to the store
          regionsToAdd.forEach((region) => addRegion(region));

          // Segment event
          segmentTrackEvent("AOIs Pasted", {
            numPasted: pasted,
            numSkipped: skipped,
            projectId,
            studyId,
            studyType,
            location: "canvas",
          });

          // Show toast based on the results of the paste action
          if (skipped > 0 && pasted === 0) {
            toast({ title: "AOIs were not pasted", description: "The AOIs are too large to fit in this asset.", type: "warning" });
          } else if (skipped > 0) {
            toast({ title: `${pasted} AOI(s) pasted, ${skipped} skipped (too large)`, type: "warning" });
          } else {
            toast({ title: `${pasted} AOI(s) pasted`, type: "success" });
          }
        }
      } else {
        // Single region paste — run through the same clamping/size-validation as paste-all
        const region: Region = payload.type === "single" ? payload.region : payload;
        if (!region.points || !Array.isArray(region.points)) throw new Error("not a Region");

        // Validate against target canvas metadata to ensure the region can fit and be clamped if necessary
        const targetMeta = allAssetRegionData[assetIndex]?.metaData;
        if (!targetMeta) return;

        // Get existing labels to ensure the pasted region gets a unique name if there's a conflict
        const existingLabels = sidePanelDisplay.map((r) => r.label);
        const { regionsToAdd, skipped } = processRegionsForPaste([region], targetMeta, existingLabels);

        // If the single region is too large to fit, inform the user and don't paste anything. Otherwise, add the region.
        if (skipped > 0) {
          toast({ title: "AOI was not pasted", description: "The AOI is too large to fit in this asset.", type: "warning" });
        } else {
          // Add the processed region to the store
          regionsToAdd.forEach((r) => addRegion(r));

          // Segment event
          segmentTrackEvent("AOIs Pasted", {
            numPasted: 1,
            numSkipped: 0,
            projectId,
            studyId,
            studyType,
            location: "canvas",
          });
          toast({ title: "AOI pasted", type: "success" });
        }
      }
    } catch {
      toast({ title: "Failed to paste AOI", type: "error" });
    }
    setContextMenuPos(null);
  }, [addRegion, setContextMenuPos, sidePanelDisplay, allAssetRegionData, assetIndex, segmentTrackEvent, projectId, studyId, studyType]);

  const handleDelete = () => {
    if (!selectedRegion) return;
    deleteRegion(selectedRegion);
    setSelectedRegion(null);
    setContextMenuPos(null);
  };

  /* Return */
  return {
    contextMenuPos,
    setContextMenuPos,
    handleContextMenu,
    handleCopy,
    handlePaste,
    handleDelete,
  };
};
